import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';
import { getBillingStatus, getBillingPortalUrl, BillingStatus } from '@/api/billing';

/**
 * Profile Subscription Card
 * Displays subscription status and management options
 */
export const SubscriptionCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [data, setData] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const billing = await getBillingStatus();
        setData(billing);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [onError]);

  const handleAction = async (action: string) => {
    try {
      setActionInProgress(true);
      if (action === 'View usage telemetry') {
        window.location.href = '/profile#telemetry';
        return;
      }
      // All billing management actions go through the Stripe portal
      const url = await getBillingPortalUrl();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading subscription information...</div>;
  }

  if (!data) {
    return <div style={{ padding: '1rem', color: '#666' }}>No subscription data available</div>;
  }

  const statusColor =
    data.status === 'active' || data.status === 'trialing' ? '#28a745' : '#dc3545';

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <strong>Status:</strong>{' '}
          <span style={{ color: statusColor, textTransform: 'capitalize' }}>
            {data.status ?? 'Unknown'}
          </span>
        </div>
        <div style={{ marginBottom: '0.5rem' }}>
          <strong>Plan:</strong>{' '}
          <span style={{ textTransform: 'capitalize' }}>{data.plan ?? 'None'}</span>
        </div>
        {data.trialEndsAt && (
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>Trial ends:</strong> {new Date(data.trialEndsAt).toLocaleDateString()}
          </div>
        )}
        {data.reason && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
            {data.reason}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {card.cardActionsList.map((action: string) => (
          <button
            key={action}
            onClick={() => handleAction(action)}
            disabled={actionInProgress}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: actionInProgress ? 'not-allowed' : 'pointer',
              opacity: actionInProgress ? 0.6 : 1,
            }}
          >
            {action}
          </button>
        ))}
      </div>
    </div>
  );
};

SubscriptionCard.displayName = 'SubscriptionCard';

export default SubscriptionCard;
